// src/components/ChatWindow.js

import React, { useState, useEffect, useRef } from 'react';
import { db, storage, functions } from '../config/firebase';
import { httpsCallable } from 'firebase/functions';
import { collection, query, where, orderBy, onSnapshot, addDoc, serverTimestamp, writeBatch, getDocs, arrayUnion, doc, updateDoc, deleteDoc, setDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { Box, TextField, Paper, Typography, Avatar, IconButton, Divider, Menu, MenuItem, LinearProgress, ListItemIcon, ListItemText, Popover } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import MicIcon from '@mui/icons-material/Mic';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ReplyIcon from '@mui/icons-material/Reply';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import TagFacesIcon from '@mui/icons-material/TagFaces';
import IosShareIcon from '@mui/icons-material/IosShare';
import PushPinIcon from '@mui/icons-material/PushPin';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SentimentSatisfiedAltIcon from '@mui/icons-material/SentimentSatisfiedAlt';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from '../context/AuthContext';
import { format } from 'date-fns';
import EmojiPicker from 'emoji-picker-react';
import Linkify from 'react-linkify'; // <-- IMPORT ADDED
import ClearChatDialog from './ClearChatDialog';
import MessageInfoDialog from './MessageInfoDialog';
import ReactionPicker from './ReactionPicker';
import DeleteOptionsDialog from './DeleteOptionsDialog';
import ForwardMessageDialog from './ForwardMessageDialog';
import { useAudioRecorder } from '../hooks/useAudioRecorder';

const ReplyPreview = ({ message, senderName }) => (
  <Paper variant="outlined" sx={{ p: 1, mb: 0.5, backgroundColor: 'rgba(255, 255, 255, 0.05)', borderLeft: '4px solid', borderColor: 'primary.main', maxWidth: '100%', }}>
    <Typography variant="caption" sx={{ fontWeight: 'bold' }}>{senderName}</Typography>
    <Typography variant="body2" color="text.secondary" noWrap>
      {message.type === 'voice' ? 'Voice Message' : message.text}
    </Typography>
  </Paper>
);

const ReactionsDisplay = ({ reactions }) => {
  if (!reactions || Object.keys(reactions).length === 0) return null;
  return (
    <Paper sx={{ mt: -1.5, mb: 0.5, p: '2px 6px', display: 'inline-flex', gap: 0.5, borderRadius: '10px', backdropFilter: 'blur(10px)', backgroundColor: 'rgba(0,0,0,0.2)' }}>
      {Object.entries(reactions).map(([emoji, uids]) => 
        uids.length > 0 && (
          <Typography key={emoji} variant="caption" sx={{fontSize: '0.9rem'}}>
            {emoji} {uids.length > 1 ? uids.length : ''}
          </Typography>
        )
      )}
    </Paper>
  );
};

const PinnedMessageBar = ({ message, onUnpin, chatPartnerName }) => {
    const { currentUser } = useAuth();
    if (!message) return null;
    const senderName = message.senderId === currentUser.uid ? "You" : chatPartnerName;
    return (
        <Paper square sx={{ p: 1, display: 'flex', alignItems: 'center', backgroundColor: 'background.default' }}>
            <PushPinIcon sx={{ mr: 2, color: 'text.secondary' }} fontSize="small" />
            <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
                <Typography variant="caption" sx={{ fontWeight: 'bold' }}>Pinned Message | {senderName}</Typography>
                <Typography variant="body2" color="text.secondary" noWrap>
                    {message.type === 'voice' ? 'Voice Message' : message.text}
                </Typography>
            </Box>
            <IconButton onClick={onUnpin} size="small"><CloseIcon fontSize="small" /></IconButton>
        </Paper>
    );
};

const LinkPreviewCard = ({ data }) => {
    if (!data || !data.title) return null;
    return (
        <Paper component="a" href={data.url} target="_blank" rel="noopener noreferrer" elevation={0}
            sx={{ 
                display: 'block', textDecoration: 'none', border: '1px solid', 
                borderColor: 'divider', borderRadius: 2, overflow: 'hidden', mt: 1 
            }}
        >
            {data.image && <Box component="img" src={data.image} sx={{ width: '100%', maxHeight: '150px', objectFit: 'cover' }} />}
            <Box sx={{ p: 1.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'text.primary' }}>{data.title}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                    {data.description}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, textTransform: 'uppercase' }}>
                    {data.siteName || new URL(data.url).hostname}
                </Typography>
            </Box>
        </Paper>
    );
};

const ChatWindow = ({ chatPartner, onBack }) => {
  const { currentUser } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [showPicker, setShowPicker] = useState(false);
  const messagesContainerRef = useRef(null);
  const [headerMenuAnchorEl, setHeaderMenuAnchorEl] = useState(null);
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  const [messageMenu, setMessageMenu] = useState({ anchorEl: null, message: null });
  const [infoDialogState, setInfoDialogState] = useState({ open: false, message: null });
  const [replyingTo, setReplyingTo] = useState(null);
  const [reactionPicker, setReactionPicker] = useState({ anchorEl: null, message: null });
  const [deleteOptionsOpen, setDeleteOptionsOpen] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState(null);
  const [pinnedMessage, setPinnedMessage] = useState(null);
  const [forwardDialog, setForwardDialog] = useState({ open: false, message: null });
  const [uploading, setUploading] = useState(false);
  const { isRecording, audioBlob, error, startRecording, stopRecording, clearRecording } = useAudioRecorder();
  const recordingTimerRef = useRef(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [isFetchingPreview, setIsFetchingPreview] = useState(false);

  useEffect(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    if (!currentUser || !chatPartner) return;
    const chatId = [currentUser.uid, chatPartner.uid].sort().join('_');
    const messagesRef = collection(db, 'chats', chatId, 'messages');
    const q = query(messagesRef, orderBy('createdAt', 'asc'));
    
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const allMessages = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const visibleMessages = allMessages.filter(msg => !msg.deletedFor?.includes(currentUser.uid));
      setMessages(visibleMessages);
      const batch = writeBatch(db);
      let updatesMade = false;
      querySnapshot.docs.forEach(document => {
        const message = document.data();
        if (message.receiverId === currentUser.uid && !message.deliveredAt) {
          batch.update(document.ref, { deliveredAt: serverTimestamp() });
          updatesMade = true;
        }
        if (message.receiverId === currentUser.uid && !message.readAt) {
          batch.update(document.ref, { readAt: serverTimestamp() });
          updatesMade = true;
        }
      });
      if (updatesMade) {
        batch.commit().catch(err => console.error("Error updating receipts:", err));
      }
    });
    return () => unsubscribe();
  }, [chatPartner, currentUser]);
  
  useEffect(() => {
    if (!currentUser || !chatPartner) return;
    const chatId = [currentUser.uid, chatPartner.uid].sort().join('_');
    const chatDocRef = doc(db, 'chats', chatId);
    const unsubscribe = onSnapshot(chatDocRef, (doc) => {
        setPinnedMessage(doc.data()?.pinnedMessage || null);
    });
    return () => unsubscribe();
  }, [chatPartner, currentUser]);
  
  if (!currentUser || !chatPartner) {
    return null; 
  }

  const chatId = [currentUser.uid, chatPartner.uid].sort().join('_');
  
  const handleStartRecording = () => {
    startRecording();
    setRecordingTime(0);
    recordingTimerRef.current = setInterval(() => {
      setRecordingTime(prevTime => {
        if (prevTime >= 19) {
          handleStopRecording();
          return 20;
        }
        return prevTime + 1;
      });
    }, 1000);
  };

  const handleStopRecording = () => {
    stopRecording();
    clearInterval(recordingTimerRef.current);
  };

  const handleSendVoiceMessage = async (blob) => {
    setUploading(true);
    const messageId = Date.now().toString();
    const filePath = `voice_messages/${chatId}/${messageId}`;
    const storageRef = ref(storage, filePath);
    try {
      await uploadBytes(storageRef, blob);
      const audioUrl = await getDownloadURL(storageRef);
      const messagesRef = collection(db, 'chats', chatId, 'messages');
      let messageData = { 
        type: 'voice', audioUrl: audioUrl, storagePath: filePath, senderId: currentUser.uid, receiverId: chatPartner.uid, 
        createdAt: serverTimestamp(), isRead: false, deliveredAt: null, readAt: null, deletedFor: [], reactions: {},
        forwardedFrom: null, linkPreview: null,
      };
      if (replyingTo) {
        messageData.replyingTo = {
          messageId: replyingTo.id, text: replyingTo.text || "Voice Message", senderId: replyingTo.senderId,
          senderName: replyingTo.senderId === currentUser.uid ? currentUser.displayName : chatPartner.displayName,
        };
      }
      await addDoc(messagesRef, messageData);
      setReplyingTo(null);
    } catch (err) {
      console.error("Error uploading voice message:", err);
    } finally {
      setUploading(false);
      clearRecording();
    }
  };

  const handleSendMessage = async (e) => {
    if (e) e.preventDefault();
    const textToSend = newMessage.trim();
    if (textToSend === '') return;
    
    setNewMessage('');
    setShowPicker(false);
    
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const foundUrl = textToSend.match(urlRegex)?.[0];
    
    let messageData = { 
      type: 'text', text: textToSend, senderId: currentUser.uid, receiverId: chatPartner.uid, 
      createdAt: serverTimestamp(), isRead: false, deliveredAt: null, readAt: null, deletedFor: [], reactions: {},
      forwardedFrom: null, linkPreview: null,
    };
    
    if (replyingTo) {
      messageData.replyingTo = {
        messageId: replyingTo.id, text: replyingTo.text || "Voice Message", senderId: replyingTo.senderId,
        senderName: replyingTo.senderId === currentUser.uid ? currentUser.displayName : chatPartner.displayName,
      };
    }
    
    const messagesRef = collection(db, 'chats', chatId, 'messages');
    const docRef = await addDoc(messagesRef, messageData);
    setReplyingTo(null);
    
    if (foundUrl) {
      setIsFetchingPreview(true);
      try {
        const getLinkPreview = httpsCallable(functions, 'getLinkPreview');
        const result = await getLinkPreview({ url: foundUrl });
        const previewData = result.data;

        if (previewData && docRef) {
          await updateDoc(docRef, { linkPreview: previewData });
        }
      } catch (err) {
        console.error("Error fetching link preview:", err);
      } finally {
        setIsFetchingPreview(false);
      }
    }
  };
  
  const handleKeyDown = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(); } };
  const onEmojiClick = (emojiObject) => { setNewMessage(prevInput => prevInput + emojiObject.emoji); };
  const handleHeaderMenuOpen = (event) => setHeaderMenuAnchorEl(event.currentTarget);
  const handleHeaderMenuClose = () => setHeaderMenuAnchorEl(null);
  const handleClearChat = async () => {
    setClearDialogOpen(false);
    try {
      const messagesRef = collection(db, 'chats', chatId, 'messages');
      const q = query(messagesRef, where('deletedFor', 'not-in', [currentUser.uid]));
      const messagesSnapshot = await getDocs(q);
      const batch = writeBatch(db);
      messagesSnapshot.forEach(doc => { batch.update(doc.ref, { deletedFor: arrayUnion(currentUser.uid) }); });
      await batch.commit();
    } catch (err) {
      console.error("Error clearing chat history:", err);
    }
  };

  const handleMessageMenuOpen = (event, message) => { setMessageMenu({ anchorEl: event.currentTarget, message }); };
  const handleMessageMenuClose = () => { setMessageMenu({ anchorEl: null, message: null }); };
  const handleCopy = () => {
    const messageToCopy = messageMenu.message;
    if (messageToCopy && messageToCopy.text) { navigator.clipboard.writeText(messageToCopy.text); }
    handleMessageMenuClose();
  };
  const handleInfo = () => { setInfoDialogState({ open: true, message: messageMenu.message }); handleMessageMenuClose(); };
  const handleStartReply = () => { setReplyingTo(messageMenu.message); handleMessageMenuClose(); };
  const handleCancelReply = () => { setReplyingTo(null); };
  const handleReactionPickerOpen = () => { setReactionPicker({ anchorEl: messageMenu.anchorEl, message: messageMenu.message }); handleMessageMenuClose(); };
  const handleReactionPickerClose = () => { setReactionPicker({ anchorEl: null, message: null }); };
  const handleReaction = async (emoji) => {
    const messageToReact = reactionPicker.message;
    if (!messageToReact) return;
    const messageRef = doc(db, 'chats', chatId, 'messages', messageToReact.id);
    const currentUserUID = currentUser.uid;
    const currentReactions = messageToReact.reactions || {};
    const reactionArray = currentReactions[emoji] || [];
    if (reactionArray.includes(currentUserUID)) {
      currentReactions[emoji] = reactionArray.filter(uid => uid !== currentUserUID);
    } else {
      currentReactions[emoji] = [...reactionArray, currentUserUID];
    }
    if(currentReactions[emoji].length === 0) { delete currentReactions[emoji]; }
    await updateDoc(messageRef, { reactions: currentReactions });
    handleReactionPickerClose();
  };
  const handleOpenDeleteOptions = () => {
    setMessageToDelete(messageMenu.message);
    setDeleteOptionsOpen(true);
    handleMessageMenuClose();
  };
  const handleDeleteForMe = async (messageToDelete) => {
    if (!messageToDelete) return;
    const messageRef = doc(db, 'chats', chatId, 'messages', messageToDelete.id);
    try {
      await updateDoc(messageRef, { deletedFor: arrayUnion(currentUser.uid) });
    } catch (err) { console.error("Error deleting for me:", err); }
    setDeleteOptionsOpen(false);
    setMessageToDelete(null);
  };
  const handleDeleteForEveryone = async (messageToDelete) => {
    if (!messageToDelete || messageToDelete.senderId !== currentUser.uid) return;
    try {
      if (messageToDelete.type === 'voice' && messageToDelete.storagePath) {
        const fileRef = ref(storage, messageToDelete.storagePath);
        await deleteObject(fileRef);
      }
      const messageRef = doc(db, 'chats', chatId, 'messages', messageToDelete.id);
      await deleteDoc(messageRef);
    } catch (err) { console.error("Error deleting for everyone:", err); }
    setDeleteOptionsOpen(false);
    setMessageToDelete(null);
  };

  const handlePinMessage = async () => {
    const messageToPin = messageMenu.message;
    if (!messageToPin) return;
    handleMessageMenuClose();
    const chatDocRef = doc(db, 'chats', chatId);
    const pinData = {
        id: messageToPin.id, text: messageToPin.text,
        type: messageToPin.type, senderId: messageToPin.senderId,
    };
    try {
        await setDoc(chatDocRef, { pinnedMessage: pinData }, { merge: true });
    } catch (err) {
        console.error("Error pinning message:", err);
    }
  };

  const handleUnpinMessage = async () => {
    const chatDocRef = doc(db, 'chats', chatId);
    try {
        await updateDoc(chatDocRef, { pinnedMessage: null });
    } catch (err) {
        console.error("Error unpinning message:", err);
    }
  };
  
  const handleOpenForwardDialog = () => {
    setForwardDialog({ open: true, message: messageMenu.message });
    handleMessageMenuClose();
  };

  const handleForwardMessage = async (selectedIds) => {
    const messageToForward = forwardDialog.message;
    if (!messageToForward || selectedIds.length === 0) return;
    
    const originalSenderName = messageToForward.senderId === currentUser.uid ? currentUser.displayName : chatPartner.displayName;
    
    const forwardedContent = {
        type: messageToForward.type,
        senderId: currentUser.uid,
        createdAt: serverTimestamp(),
        isRead: false, deliveredAt: null, readAt: null, deletedFor: [], reactions: {},
        forwardedFrom: { senderName: originalSenderName }
    };

    if (messageToForward.type === 'text') {
        forwardedContent.text = messageToForward.text;
    } else if (messageToForward.type === 'voice') {
        forwardedContent.audioUrl = messageToForward.audioUrl;
        forwardedContent.storagePath = messageToForward.storagePath;
    }

    for (const recipientId of selectedIds) {
        const newChatId = [currentUser.uid, recipientId].sort().join('_');
        const messagesRef = collection(db, 'chats', newChatId, 'messages');
        try {
            await addDoc(messagesRef, { ...forwardedContent, receiverId: recipientId });
        } catch (err) {
            console.error(`Failed to forward message to ${recipientId}:`, err);
        }
    }
  };

  const renderInput = () => {
    if (isRecording) {
      return ( <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}> <IconButton color="error" onClick={handleStopRecording}><StopCircleIcon /></IconButton> <Typography variant="body2" color="error" sx={{ flexGrow: 1, fontVariantNumeric: 'tabular-nums' }}> Recording... {recordingTime}s / 20s </Typography> </Box> );
    }
    if (audioBlob) {
      return ( <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1 }}> <IconButton onClick={clearRecording}><DeleteIcon /></IconButton> <audio controls src={URL.createObjectURL(audioBlob)} style={{ flexGrow: 1 }}></audio> <IconButton color="primary" onClick={() => handleSendVoiceMessage(audioBlob)}><SendIcon /></IconButton> </Box> );
    }
    return (
      <>
        <IconButton onClick={() => setShowPicker(val => !val)}><SentimentSatisfiedAltIcon /></IconButton>
        <TextField multiline maxRows={4} fullWidth variant="standard" placeholder="Type a message..." value={newMessage} onChange={(e) => setNewMessage(e.target.value)} onKeyDown={handleKeyDown} onFocus={() => setShowPicker(false)} sx={{ ml: 1, alignSelf: 'center' }} InputProps={{ disableUnderline: true }} autoComplete="off" />
        {newMessage ? (<IconButton type="submit" color="primary"><SendIcon /></IconButton>) : (<IconButton color="primary" onClick={handleStartRecording}><MicIcon /></IconButton>)}
      </>
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
      <Paper square sx={{ p: 2, display: 'flex', alignItems: 'center' }}>
        <IconButton onClick={onBack} sx={{ mr: 1 }}><ArrowBackIcon /></IconButton>
        <Avatar src={chatPartner.photoURL} sx={{ mr: 2 }} />
        <Typography variant="h6" sx={{ flexGrow: 1 }}>{chatPartner.displayName}</Typography>
        <IconButton onClick={handleHeaderMenuOpen}><MoreVertIcon /></IconButton>
        <Menu anchorEl={headerMenuAnchorEl} open={Boolean(headerMenuAnchorEl)} onClose={handleHeaderMenuClose}><MenuItem onClick={() => { setClearDialogOpen(true); handleHeaderMenuClose(); }}>Clear history for me</MenuItem></Menu>
      </Paper>
      <Divider />
      {pinnedMessage && <PinnedMessageBar message={pinnedMessage} onUnpin={handleUnpinMessage} chatPartnerName={chatPartner.displayName} />}
      {uploading && <Box sx={{ p: 1 }}><Typography variant="caption" sx={{textAlign: 'center', width: '100%'}}>Uploading voice note...</Typography><LinearProgress color="primary" /></Box>}
      {isFetchingPreview && <LinearProgress color="secondary" />}
      <Box ref={messagesContainerRef} sx={{ flexGrow: 1, overflowY: 'auto', p: 2, position: 'relative' }}>
        {messages.map(msg => (
          <Box key={msg.id} sx={{ display: 'flex', justifyContent: msg.senderId === currentUser.uid ? 'flex-end' : 'flex-start', mb: 1, '&:hover .message-actions': { opacity: 1 } }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: msg.senderId === currentUser.uid ? 'flex-end' : 'flex-start' }}>
              {msg.forwardedFrom && (
                <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', mb: 0.5, opacity: 0.8, pl: 1 }}>
                    <IosShareIcon sx={{ fontSize: '1rem', mr: 0.5, transform: 'scaleX(-1)' }} />
                    Forwarded
                </Typography>
              )}
              <Paper variant="elevation" elevation={2} onClick={(e) => handleMessageMenuOpen(e, msg)} sx={{ cursor: 'pointer', p: 0, backgroundColor: msg.senderId === currentUser.uid ? 'primary.main' : 'background.paper', color: msg.senderId === currentUser.uid ? 'primary.contrastText' : 'text.primary', borderRadius: 2, maxWidth: '700px', position: 'relative' }}>
                {msg.replyingTo && (<Box sx={{ px: 1.5, pt: 1, pb: 0.5 }}><ReplyPreview message={msg.replyingTo} senderName={msg.replyingTo.senderId === currentUser.uid ? "You" : chatPartner.displayName} /></Box>)}
                <Box sx={{ p: msg.type === 'voice' ? 1 : 1.5, pt: msg.replyingTo ? 0.5 : (msg.type === 'voice' ? 1 : 1.5) }}>
                  {msg.type === 'voice' ? ( <audio controls src={msg.audioUrl}></audio> ) : ( 
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      <Linkify componentDecorator={(decoratedHref, decoratedText, key) => (
                        <a target="_blank" rel="noopener noreferrer" href={decoratedHref} key={key} style={{ color: 'inherit', textDecoration: 'underline' }}>
                          {decoratedText}
                        </a>
                      )}>
                        {msg.text}
                      </Linkify>
                    </Typography>
                   )}
                  {msg.linkPreview && <LinkPreviewCard data={msg.linkPreview} />}
                </Box>
                <Box sx={{ position: 'absolute', bottom: -12, right: 6 }}><ReactionsDisplay reactions={msg.reactions} /></Box>
              </Paper>
              {msg.createdAt && (<Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, px: 0.5 }}>{format(msg.createdAt.toDate(), 'p')}</Typography>)}
            </Box>
            <IconButton className="message-actions" onClick={(e) => handleMessageMenuOpen(e, msg)} size="small" sx={{ opacity: 0, transition: 'opacity 0.2s', alignSelf: 'center' }}><ArrowDropDownIcon /></IconButton>
          </Box>
        ))}
      </Box>
      <Box sx={{ position: 'relative' }}>
        {replyingTo && (<Paper square sx={{ p: 1, display: 'flex', alignItems: 'center', backgroundColor: 'background.default' }}><Box sx={{ flexGrow: 1, ml: 1 }}><ReplyPreview message={replyingTo} senderName={replyingTo.senderId === currentUser.uid ? "You" : chatPartner.displayName} /></Box><IconButton onClick={handleCancelReply}><CloseIcon /></IconButton></Paper>)}
        {showPicker && ( <Box sx={{ position: 'absolute', bottom: '60px', left: 0, right: 0, zIndex: 1 }}> <EmojiPicker onEmojiClick={onEmojiClick} emojiStyle="google" height={350} width="100%" theme="dark" /> </Box>)}
        <Divider />
        <Paper square component="form" onSubmit={handleSendMessage} sx={{ p: '2px 4px', display: 'flex', alignItems: 'center' }}>
          {renderInput()}
        </Paper>
        {error && <Typography variant="caption" color="error" sx={{ textAlign: 'center', width: '100%', display: 'block', p: 1 }}>{error}</Typography>}
      </Box>
      <Menu anchorEl={messageMenu.anchorEl} open={Boolean(messageMenu.anchorEl)} onClose={handleMessageMenuClose}>
        {messageMenu.message?.senderId === currentUser.uid && <MenuItem onClick={handleInfo}><ListItemIcon><InfoOutlinedIcon fontSize="small" /></ListItemIcon><ListItemText>Message info</ListItemText></MenuItem>}
        <MenuItem onClick={handleStartReply}><ListItemIcon><ReplyIcon fontSize="small" /></ListItemIcon><ListItemText>Reply</ListItemText></MenuItem>
        {messageMenu.message?.type === 'text' && <MenuItem onClick={handleCopy}><ListItemIcon><ContentCopyIcon fontSize="small" /></ListItemIcon><ListItemText>Copy</ListItemText></MenuItem>}
        <MenuItem onClick={handleReactionPickerOpen}><ListItemIcon><TagFacesIcon fontSize="small" /></ListItemIcon><ListItemText>React</ListItemText></MenuItem>
        <MenuItem onClick={handleOpenForwardDialog}><ListItemIcon><IosShareIcon fontSize="small" /></ListItemIcon><ListItemText>Forward</ListItemText></MenuItem>
        <MenuItem onClick={handlePinMessage}><ListItemIcon><PushPinIcon fontSize="small" /></ListItemIcon><ListItemText>Pin</ListItemText></MenuItem>
        <Divider />
        <MenuItem onClick={handleOpenDeleteOptions} sx={{ color: 'error.main' }}><ListItemIcon><DeleteOutlineIcon fontSize="small" color="error" /></ListItemIcon><ListItemText>Delete</ListItemText></MenuItem>
      </Menu>
      <Popover open={Boolean(reactionPicker.anchorEl)} anchorEl={reactionPicker.anchorEl} onClose={handleReactionPickerClose} anchorOrigin={{ vertical: 'top', horizontal: 'center' }} transformOrigin={{ vertical: 'bottom', horizontal: 'center' }} >
        <ReactionPicker onSelectReaction={handleReaction} />
      </Popover>
      <MessageInfoDialog open={infoDialogState.open} onClose={() => setInfoDialogState({ open: false, message: null })} message={infoDialogState.message} />
      <ClearChatDialog open={clearDialogOpen} onClose={() => setClearDialogOpen(false)} onConfirm={handleClearChat} chatPartnerName={chatPartner.displayName} />
      <DeleteOptionsDialog open={deleteOptionsOpen} onClose={() => { setDeleteOptionsOpen(false); setMessageToDelete(null); }} onDeleteForMe={handleDeleteForMe} onDeleteForEveryone={handleDeleteForEveryone} message={messageToDelete}/>
      <ForwardMessageDialog
        open={forwardDialog.open}
        onClose={() => setForwardDialog({ open: false, message: null })}
        onForward={handleForwardMessage}
      />
    </Box>
  );
};

export default ChatWindow;